import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { BarChart3, Users, BookOpen, FileText, CheckCircle2, Clock, TrendingUp } from 'lucide-react';
import { Progress } from '../components/ui/progress';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Analytics() {
    const { user } = useAuth();
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const endpoint = user.role === 'instructor' ? 'instructor' : 'student';
                const res = await axios.get(`${API}/analytics/${endpoint}`);
                setAnalytics(res.data);
            } catch (e) {
                toast.error('Failed to load analytics');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [user.role]);

    if (loading) {
        return <div className="p-8"><div className="h-64 skeleton rounded-2xl" /></div>;
    }

    if (!analytics) {
        return (
            <div className="text-center py-12">
                <BarChart3 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Unable to load analytics</p>
            </div>
        );
    }

    const isInstructor = user.role === 'instructor';

    return (
        <div className="space-y-8" data-testid="analytics-page">
            <div>
                <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900">Analytics</h1>
                <p className="text-slate-600">{isInstructor ? 'Course performance overview' : 'Your academic performance'}</p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {isInstructor ? (
                    <>
                        <StatCard icon={BookOpen} label="Courses" value={analytics.total_courses} color="bg-[#0F2A44]" />
                        <StatCard icon={Users} label="Students" value={analytics.total_students} color="bg-[#2F8FCB]" />
                        <StatCard icon={CheckCircle2} label="Graded" value={analytics.submissions_graded} color="bg-emerald-500" />
                        <StatCard icon={Clock} label="Pending" value={analytics.submissions_pending} color="bg-amber-500" />
                    </>
                ) : (
                    <>
                        <StatCard icon={BookOpen} label="Courses" value={analytics.enrolled_courses} color="bg-[#0F2A44]" />
                        <StatCard icon={FileText} label="Assignments" value={analytics.total_assignments} color="bg-[#2F8FCB]" />
                        <StatCard icon={CheckCircle2} label="Submitted" value={analytics.submitted} color="bg-emerald-500" />
                        <StatCard icon={TrendingUp} label="Avg Grade" value={`${analytics.average_grade}%`} color="bg-violet-500" />
                    </>
                )}
            </div>

            {isInstructor && analytics.course_stats && (
                <div className="bg-white rounded-2xl border overflow-hidden">
                    <div className="p-4 border-b">
                        <h2 className="font-semibold">Course Details</h2>
                    </div>
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50">
                            <tr>
                                <th className="text-left p-3">Course</th>
                                <th className="text-center p-3">Students</th>
                                <th className="text-center p-3">Graded</th>
                                <th className="text-center p-3">Pending</th>
                                <th className="p-3">Progress</th>
                            </tr>
                        </thead>
                        <tbody>
                            {analytics.course_stats.map((c, i) => {
                                const total = c.graded + c.pending;
                                const pct = total > 0 ? Math.round((c.graded / total) * 100) : 0;
                                return (
                                    <tr key={i} className="border-b">
                                        <td className="p-3 font-medium">{c.course_title}</td>
                                        <td className="p-3 text-center">{c.enrolled_students}</td>
                                        <td className="p-3 text-center text-emerald-600">{c.graded}</td>
                                        <td className="p-3 text-center text-amber-600">{c.pending}</td>
                                        <td className="p-3">
                                            <div className="flex items-center gap-2">
                                                <Progress value={pct} className="flex-1 h-2" />
                                                <span className="text-xs w-10">{pct}%</span>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {!isInstructor && (
                <div className="bg-white rounded-2xl border p-6">
                    <h2 className="font-semibold mb-4">Accounting Practice</h2>
                    <div className="flex items-center gap-4">
                        <div className="w-16 h-16 rounded-xl bg-[#E0F2FE] flex items-center justify-center">
                            <FileText className="w-8 h-8 text-[#2F8FCB]" />
                        </div>
                        <div>
                            <p className="text-3xl font-bold">{analytics.journal_entries}</p>
                            <p className="text-slate-500 text-sm">Journal Entries</p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function StatCard({ icon: Icon, label, value, color }) {
    return (
        <div className="bg-white rounded-xl border p-4">
            <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center mb-3`}>
                <Icon className="w-5 h-5 text-white" />
            </div>
            <p className="text-2xl font-bold">{value}</p>
            <p className="text-sm text-slate-500">{label}</p>
        </div>
    );
}
